
import React, { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Calculator, TrendingUp } from "lucide-react";

const New2Calculator = () => {
  const { language } = useLanguage();
  const [monthlyAmount, setMonthlyAmount] = useState([50]);
  const [years, setYears] = useState([15]);
  
  const content = {
    fr: {
      title: "Calculez l'impact de votre cadeau",
      subtitle: "Découvrez comment votre contribution peut grandir dans le temps",
      monthlyLabel: "Contribution mensuelle",
      yearsLabel: "Durée (années)",
      totalContributed: "Total contribué",
      estimatedValue: "Valeur estimée*",
      disclaimer: "*Estimation basée sur un rendement annuel moyen de 5%. Les performances passées ne garantissent pas les résultats futurs.",
      startNow: "Commencer maintenant"
    },
    en: {
      title: "Calculate your gift's impact",
      subtitle: "Discover how your contribution can grow over time",
      monthlyLabel: "Monthly contribution",
      yearsLabel: "Duration (years)",
      totalContributed: "Total contributed",
      estimatedValue: "Estimated value*",
      disclaimer: "*Estimate based on 5% average annual return. Past performance does not guarantee future results.",
      startNow: "Start now"
    },
    de: {
      title: "Berechnen Sie die Wirkung Ihres Geschenks",
      subtitle: "Entdecken Sie, wie Ihr Beitrag im Laufe der Zeit wachsen kann",
      monthlyLabel: "Monatlicher Beitrag",
      yearsLabel: "Dauer (Jahre)",
      totalContributed: "Gesamtbeitrag",
      estimatedValue: "Geschätzter Wert*",
      disclaimer: "*Schätzung basiert auf 5% durchschnittlicher jährlicher Rendite. Vergangene Leistungen garantieren keine zukünftigen Ergebnisse.",
      startNow: "Jetzt starten"
    }
  };

  const calculateValue = () => {
    const monthly = monthlyAmount[0];
    const duration = years[0];
    const annualRate = 0.05;
    const monthlyRate = annualRate / 12;
    const totalMonths = duration * 12;
    
    // Future value of annuity formula
    const futureValue = monthly * (Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate;
    return Math.round(futureValue);
  };

  const totalContributed = monthlyAmount[0] * years[0] * 12;
  const estimatedValue = calculateValue();

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex justify-center mb-6">
            <div className="bg-legatia-orange/10 p-4 rounded-full">
              <Calculator className="h-8 w-8 text-legatia-orange" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {content[language].title}
          </h2>
          <p className="text-xl text-gray-600">
            {content[language].subtitle}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="bg-white shadow-xl border-0">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-2xl font-bold">
                <TrendingUp className="inline-block mr-2 h-6 w-6 text-legatia-orange" />
                Simulateur d'impact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-4">
                      {content[language].monthlyLabel}: €{monthlyAmount[0]}
                    </label>
                    <Slider
                      value={monthlyAmount}
                      onValueChange={setMonthlyAmount}
                      max={500}
                      min={25}
                      step={25}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>€25</span>
                      <span>€500</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-4">
                      {content[language].yearsLabel}: {years[0]} ans
                    </label>
                    <Slider
                      value={years}
                      onValueChange={setYears}
                      max={20}
                      min={5}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>5 ans</span>
                      <span>20 ans</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="text-center space-y-4">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">{content[language].totalContributed}</div>
                        <div className="text-2xl font-bold text-gray-900">€{totalContributed.toLocaleString()}</div>
                      </div>
                      
                      <div className="border-t pt-4">
                        <div className="text-sm text-gray-600 mb-1">{content[language].estimatedValue}</div>
                        <div className="text-3xl font-bold bg-gradient-to-r from-legatia-orange to-legatia-red bg-clip-text text-transparent">
                          €{estimatedValue.toLocaleString()}
                        </div>
                        <div className="text-sm text-legatia-green font-medium">
                          +€{(estimatedValue - totalContributed).toLocaleString()} gain potentiel
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-legatia-orange to-legatia-red hover:from-legatia-orange/90 hover:to-legatia-red/90 text-white py-6 text-lg rounded-full"
                    onClick={() => window.location.href = "https://app.getlegatia.ch/"}
                  >
                    {content[language].startNow}
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-6">
                {content[language].disclaimer}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default New2Calculator;
